import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Settings, Download, Lock, LockOpen, Zap } from 'lucide-react';
import type { AppLockInstance } from '@ascend/applock';
import { useAppStore } from '@/store';
import { Button } from './shared';
import { LockMenu } from './AppLock';
import { QuickLogModal } from './QuickLog/QuickLogModal';

export interface HeaderProps {
  onOpenSettings: () => void;
  onOpenExportImport: () => void;
  onStartWizard: () => void;
  /** Opens the demo dataset picker. When omitted/undefined, the nav link is hidden. */
  onOpenLoadDemo?: () => void;
  // AppLock controls (deferred-encryption pattern). Typed with explicit
  // `| undefined` so callers may pass `undefined` under exactOptionalPropertyTypes.
  lockInstance?: AppLockInstance | undefined;
  isLockEnabled?: boolean | undefined;
  onEnableLock?: (() => void) | undefined;
}

export function Header({
  onOpenSettings,
  onOpenExportImport,
  onStartWizard,
  onOpenLoadDemo,
  lockInstance,
  isLockEnabled,
  onEnableLock,
}: HeaderProps): React.ReactElement {
  const courses = useAppStore((s) => s.courses);
  const selectedCourseId = useAppStore((s) => s.selectedCourseId);
  const selectCourse = useAppStore((s) => s.selectCourse);
  const showLoadDemo = useAppStore((s) => s.settings.showLoadDemo !== false);

  const [moreOpen, setMoreOpen] = useState(false);
  const [lockSlotOpen, setLockSlotOpen] = useState(false);
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Close the overflow on outside click / Escape.
  useEffect(() => {
    if (!moreOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moreOpen]);

  const lockEnabled = !!isLockEnabled && !!lockInstance;
  const showLockAffordance = lockEnabled || !!onEnableLock;

  const handleLockItem = () => {
    setMoreOpen(false);
    if (lockEnabled) {
      // Summon the lock-management control in place of the overflow trigger.
      setLockSlotOpen(true);
    } else if (onEnableLock) {
      onEnableLock();
    }
  };

  return (
    <header className="header" role="banner">
      <div className="header__brand">
        <h1 className="header__logo">Absence Budget</h1>
        <span className="header__subtitle">Attendance budget tracker</span>
      </div>

      <nav className="header__nav" role="navigation" aria-label="Course navigation">
        <select
          className="header__course-select"
          value={selectedCourseId ?? ''}
          onChange={(e) => selectCourse(e.target.value || null)}
          aria-label="Select course"
        >
          <option value="">Dashboard</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
      </nav>

      <div className="header__actions">
        {selectedCourseId && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setQuickLogOpen(true)}
            aria-label="Quick log today's absence"
            className="header__quick-log"
          >
            <Zap size={16} aria-hidden="true" />
            Quick Log
          </Button>
        )}
        {showLoadDemo && onOpenLoadDemo && (
          <Button variant="ghost" size="sm" onClick={onOpenLoadDemo} aria-label="Load demo dataset">
            Load Demo
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onStartWizard}>
          + Add Course
        </Button>

        {quickLogOpen && (
          <QuickLogModal isOpen={quickLogOpen} onClose={() => setQuickLogOpen(false)} />
        )}

        {lockSlotOpen && lockInstance ? (
          <LockMenu
            lock={lockInstance}
            defaultOpen
            onClose={() => setLockSlotOpen(false)}
          />
        ) : (
          <div className="header__overflow" ref={moreRef}>
            <button
              type="button"
              className="header__icon-btn"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              aria-label="More actions"
              onClick={() => setMoreOpen((v) => !v)}
            >
              <MoreVertical size={20} aria-hidden="true" />
            </button>
            {moreOpen && (
              <div className="header__overflow-dropdown" role="menu" aria-label="More actions">
                <button
                  type="button"
                  role="menuitem"
                  className="header__overflow-item"
                  onClick={() => {
                    setMoreOpen(false);
                    onOpenExportImport();
                  }}
                >
                  <Download size={16} aria-hidden="true" />
                  <span>Export / Import</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="header__overflow-item"
                  onClick={() => {
                    setMoreOpen(false);
                    onOpenSettings();
                  }}
                >
                  <Settings size={16} aria-hidden="true" />
                  <span>Settings</span>
                </button>
                {showLockAffordance && (
                  <button
                    type="button"
                    role="menuitem"
                    className="header__overflow-item"
                    onClick={handleLockItem}
                  >
                    {lockEnabled ? (
                      <Lock size={16} aria-hidden="true" />
                    ) : (
                      <LockOpen size={16} aria-hidden="true" />
                    )}
                    <span>{lockEnabled ? 'App Lock' : 'Enable App Lock'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
